import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Package, ClipboardCheck, CheckCircle2, ShoppingBag, ArrowRight, TrendingUp, Loader2,
  CalendarClock, Gauge, Flame, Rocket, DollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { TYPE_LABELS } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const StatCard = ({ icon: Icon, label, value, tint, testid, onClick }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="text-left bg-white border border-zinc-200 rounded-lg p-5 hover:border-zinc-300 hover:shadow-sm transition-all duration-200 group"
  >
    <div className="flex items-center justify-between">
      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${tint}`}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <ArrowRight className="w-4 h-4 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
    </div>
    <div className="mt-4 font-display text-3xl font-bold tracking-tight">{value ?? "—"}</div>
    <div className="text-xs text-zinc-500 mt-0.5">{label}</div>
  </button>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opps, setOpps] = useState([]);
  const [building, setBuilding] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).finally(() => setLoading(false));
    api.get("/opportunities?only_unbuilt=true").then(({ data }) => setOpps(data.items.slice(0, 3))).catch(() => {});
  }, []);

  const buildOpp = async (idea) => {
    setBuilding(idea.id);
    toast.info("Building full package… ~20s");
    try {
      const { data } = await api.post("/products/build", { idea_id: idea.id, generate_mockup: true });
      toast.success("Package ready");
      setOpps((prev) => prev.filter((o) => o.id !== idea.id));
      navigate(`/products?id=${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    } finally { setBuilding(null); }
  };

  const firstName = (user?.name || "there").split(" ")[0];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline text-blue-600">Pipeline Overview</p>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Welcome back, {firstName}</h1>
          <p className="text-sm text-zinc-500 mt-1">Here's the state of your multi-channel product engine.</p>
        </div>
        <button
          onClick={() => navigate("/ideas")}
          data-testid="cta-generate-ideas"
          className="h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium flex items-center gap-2 transition-colors"
        >
          <Sparkles className="w-4 h-4" /> Generate product ideas
        </button>
      </div>

      {opps.length > 0 && (
        <div className="rounded-lg p-5 bg-zinc-900 text-white relative overflow-hidden" data-testid="hero-opportunities">
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-orange-500/20 blur-3xl" />
          <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" />
              <h2 className="font-display text-lg font-bold tracking-tight">Your best moves right now</h2>
            </div>
            <button onClick={() => navigate("/opportunities")} data-testid="hero-see-all" className="text-xs text-zinc-300 hover:text-white font-medium">See all →</button>
          </div>
          <div className="grid md:grid-cols-3 gap-3 mt-4 relative">
            {opps.map((o, i) => (
              <div key={o.id} data-testid={`hero-opp-${o.id}`} className="bg-white/5 border border-white/10 rounded-lg p-3 backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="font-display font-bold text-orange-400">#{i + 1}</span>
                    <Badge className="text-[9px] bg-white/10 text-white hover:bg-white/10 border-0">{TYPE_LABELS[o.product_type]}</Badge>
                    {o.hot && <Badge className="text-[9px] bg-orange-500 text-white hover:bg-orange-500 border-0">Hot</Badge>}
                  </div>
                  <span className="font-display text-lg font-bold text-blue-300 leading-none">{o.opportunity_score}</span>
                </div>
                <div className="text-sm font-semibold mt-2 leading-tight line-clamp-2 min-h-[2.5rem]">{o.title}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-400">
                  <span>{o.theme_name}</span><span>·</span><span>★ {o.impulse_score}</span>
                </div>
                <button onClick={() => buildOpp(o)} disabled={building === o.id} data-testid={`hero-build-${o.id}`}
                  className="w-full mt-3 h-8 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-xs font-medium flex items-center justify-center gap-1.5 transition-colors">
                  {building === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />}
                  {building === o.id ? "Building…" : "Build package"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Sparkles} label="Concepts generated" value={stats?.ideas} tint="bg-purple-100 text-purple-700" testid="stat-ideas" onClick={() => navigate("/ideas")} />
        <StatCard icon={ClipboardCheck} label="Awaiting review" value={stats?.review} tint="bg-amber-100 text-amber-700" testid="stat-review" onClick={() => navigate("/approval")} />
        <StatCard icon={CheckCircle2} label="Published live" value={stats?.published} tint="bg-emerald-100 text-emerald-700" testid="stat-published" onClick={() => navigate("/approval")} />
        <StatCard icon={ShoppingBag} label="Orders needing info" value={stats?.orders_pending} tint="bg-rose-100 text-rose-700" testid="stat-orders" onClick={() => navigate("/orders")} />
      </div>

      {/* Sales & fulfillment */}
      <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="sales-widget">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <DollarSign className="w-4.5 h-4.5 text-emerald-600" /> Sales &amp; fulfillment
          </h2>
          <button onClick={() => navigate("/store-orders")} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Store orders →</button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-4">
          {[
            ["Revenue (all)", `$${Number(stats?.sales?.total_revenue || 0).toFixed(2)}`, "text-emerald-700"],
            ["Revenue (7d)", `$${Number(stats?.sales?.week_revenue || 0).toFixed(2)}`, "text-emerald-700"],
            ["Orders (7d)", stats?.sales?.orders_week ?? 0, "text-zinc-900"],
            ["In production", stats?.sales?.in_production ?? 0, "text-blue-700"],
            ["Shipped / done", stats?.sales?.shipped ?? 0, "text-violet-700"],
          ].map(([label, v, cls]) => (
            <div key={label} className="border border-zinc-200 rounded-md p-3.5" data-testid={`sales-${label}`}>
              <div className={`font-display text-2xl font-bold tracking-tight ${cls}`}>{v}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
        {(stats?.sales?.recent_orders || []).length > 0 ? (
          <div className="mt-5">
            <div className="overline text-zinc-400 mb-2">Recent orders</div>
            <div className="space-y-1.5">
              {stats.sales.recent_orders.map((o) => (
                <button key={o.id} onClick={() => navigate(o.type === "wholesale" ? "/wholesale" : "/store-orders")} className="w-full flex items-center justify-between text-left py-2 px-3 rounded-md hover:bg-zinc-50 border border-transparent hover:border-zinc-200 transition-colors" data-testid={`recent-order-${o.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className="text-[9px] bg-zinc-100 text-zinc-500 border-zinc-200 shrink-0">{o.type === "wholesale" ? "Wholesale" : "Store"}</Badge>
                    <span className="text-sm font-medium truncate">{o.buyer}</span>
                    <span className="text-[11px] text-zinc-400 truncate hidden sm:inline">#{o.ref}</span>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge className="text-[9px] bg-blue-50 text-blue-700 border-blue-200 capitalize">{String(o.status || "").replace(/[-_]/g, " ")}</Badge>
                    <span className="text-sm font-bold tabular-nums">${Number(o.subtotal).toFixed(2)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-400 mt-4 text-center py-4">No sales yet — orders from Shopify/Printify and wholesale will appear here.</p>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Pipeline funnel */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-5">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <Package className="w-4.5 h-4.5 text-zinc-400" /> Product pipeline
          </h2>
          <div className="mt-5 grid grid-cols-4 gap-3 text-center">
            {[
              ["Ideas", stats?.ideas, "bg-purple-50 text-purple-700"],
              ["In review", stats?.review, "bg-amber-50 text-amber-700"],
              ["Drafts", stats?.drafts, "bg-blue-50 text-blue-700"],
              ["Published", stats?.published, "bg-emerald-50 text-emerald-700"],
            ].map(([label, v, cls]) => (
              <div key={label} className={`rounded-md py-4 ${cls}`}>
                <div className="font-display text-2xl font-bold">{v ?? 0}</div>
                <div className="text-[11px] mt-0.5 opacity-80">{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-6">
            <div className="overline text-zinc-400 mb-3">Fulfillment queue</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                ["Needs info", stats?.orders_pending],
                ["In design", stats?.orders_design],
                ["Ready / done", stats?.orders_ready],
              ].map(([l, v]) => (
                <div key={l} className="border border-zinc-200 rounded-md p-3">
                  <div className="font-display text-xl font-bold">{v ?? 0}</div>
                  <div className="text-[11px] text-zinc-500">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Theme performance */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <TrendingUp className="w-4.5 h-4.5 text-zinc-400" /> Top themes
          </h2>
          <p className="text-xs text-zinc-400 mt-1">By avg impulse-buy score</p>
          <div className="mt-4 space-y-3">
            {(stats?.theme_performance || []).slice(0, 6).map((t) => (
              <div key={t.theme}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-zinc-700 font-medium truncate">{t.theme}</span>
                  <span className="text-zinc-500">{t.avg_score}</span>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600 rounded-full" style={{ width: `${t.avg_score}%` }} />
                </div>
              </div>
            ))}
            {(!stats?.theme_performance || stats.theme_performance.length === 0) && (
              <p className="text-sm text-zinc-400 py-6 text-center">Build products to see theme performance.</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-5" data-testid="upcoming-events-widget">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
              <CalendarClock className="w-4.5 h-4.5 text-zinc-400" /> Upcoming on the calendar
            </h2>
            <button onClick={() => navigate("/calendar")} className="text-xs text-blue-600 hover:text-blue-700 font-medium">Open engine →</button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {(stats?.upcoming_events || []).map((e) => (
              <button key={e.key} onClick={() => navigate("/calendar")} className="text-left border border-zinc-200 rounded-md p-3 hover:border-blue-300 hover:bg-blue-50/30 transition-colors">
                <div className="text-2xl">{e.emoji}</div>
                <div className="text-xs font-semibold mt-1 leading-tight">{e.name}</div>
                <div className="text-[11px] text-orange-500 font-medium mt-0.5">{e.days_until} days away</div>
              </button>
            ))}
            {(!stats?.upcoming_events || !stats.upcoming_events.length) && <p className="text-sm text-zinc-400">No events.</p>}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="ai-spend-widget">
          <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
            <Gauge className="w-4.5 h-4.5 text-zinc-400" /> AI spend
          </h2>
          <p className="text-xs text-zinc-400 mt-1">Estimated, this month</p>
          <div className="font-display text-3xl font-bold tracking-tight mt-3">${Number(stats?.month_ai_spend || 0).toFixed(2)}</div>
          {stats?.monthly_cost_limit != null ? (
            <>
              <div className="text-xs text-zinc-500 mt-1">of ${Number(stats.monthly_cost_limit).toFixed(2)} limit</div>
              <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden mt-2">
                <div className="h-full bg-blue-600" style={{ width: `${Math.min((stats.month_ai_spend / stats.monthly_cost_limit) * 100, 100)}%` }} />
              </div>
            </>
          ) : <div className="text-xs text-zinc-400 mt-1">No limit set · <button onClick={() => navigate("/admin")} className="text-blue-600">set one</button></div>}
          <div className="mt-4 pt-3 border-t border-zinc-100 flex justify-between text-xs">
            <span className="text-zinc-500">Social posts to review</span>
            <span className="font-bold text-amber-600">{stats?.social_review || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
